import React, { useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";
import { Controller } from "react-hook-form";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FindingsCard from "./FindingsCard";

export default function QcFindingsRecommendationsSection({
  mode = "edit",
  control,
  register,
  setValue,
  clearErrors,
  errors = {},

  pestTypes = [],
  levels = [],
  infestationFindings = {},
  onInfestationChange,
  ratingBtnSx,

  findingsAreas = [],
  appendFindingArea,
  removeFindingArea,
  maxFindingsAreas = 6,

  recFields = [],
  recValues = [],
  appendRec,
  removeRec,
  recommendationOptions = [],
  maxRecommendations = 8,
}) {
  const readOnly = mode === "view";

  const createEmptyFindingArea = () => ({
    location: "",
    pests: {
      Roaches: 0,
      Mosq: 0,
      Rodents: 0,
      Flies: 0,
      Ants: 0,
      Others: 0,
    },
  });

  useEffect(() => {
    if (!readOnly && findingsAreas.length === 0) {
      appendFindingArea(createEmptyFindingArea());
    }
  }, [readOnly, findingsAreas.length, appendFindingArea]);

  return (
    <Grid container spacing={2}>
      {/* FINDINGS */}
      <Grid item xs={12}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader
            title={
              <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                Findings
              </Typography>
            }
            sx={{ pb: 0, px: 2, pt: 2 }}
          />
          <CardContent sx={{ px: 2, pb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Degree of Infestation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              L - Light, M - Medium, H - High
            </Typography>

            <Grid container spacing={1.5}>
              {pestTypes.map((pest) => (
                <Grid item xs={12} key={pest}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      flexWrap: "wrap",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1.25,
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {pest}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 4 }}>
                      {levels.map((lv) => {
                        const selected = infestationFindings[pest] === lv;
                        return (
                          <Button
                            key={lv}
                            type="button"
                            onClick={() => {
                              if (readOnly) return;
                              const nextValue = selected ? "" : lv;
                              onInfestationChange(pest, nextValue);
                            }}
                            // sx={ratingBtnSx(selected)}
                            aria-label={`${pest} ${lv}`}
                            // disabled={readOnly}
                            sx={ratingBtnSx(selected, lv)}
                          >
                            {lv}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 2 }}>
              {findingsAreas.map((f, idx) => (
                <FindingsCard
                  key={f.id}
                  control={control}
                  register={register}
                  index={idx}
                  canRemove={!readOnly && findingsAreas.length > 1}
                  removeFinding={() => {
                    if (!readOnly && findingsAreas.length > 1) {
                      removeFindingArea(idx);
                    }
                  }}
                  readOnly={readOnly}
                />
              ))}

              {!readOnly && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={findingsAreas.length >= maxFindingsAreas}
                  onClick={() => {
                    if (findingsAreas.length >= maxFindingsAreas) return;
                    appendFindingArea(createEmptyFindingArea());
                  }}
                >
                  Add Area ({findingsAreas.length}/{maxFindingsAreas})
                </Button>
              )}
            </Box>

            {errors?.findingsAreas?.message && (
              <FormHelperText error sx={{ mt: 1 }}>
                {errors.findingsAreas.message}
              </FormHelperText>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* RECOMMENDATIONS */}
      <Grid item xs={12}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader
            title={
              <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                Recommendations
              </Typography>
            }
            sx={{ pb: 0, px: 2, pt: 2 }}
          />
          <CardContent sx={{ px: 2, pb: 2 }}>
            <Grid container spacing={1.5}>
              {recFields.map((f, idx) => {
                const selected = recValues?.[idx]?.option ?? "";

                return (
                  <Grid item xs={12} key={f.id}>
                    <Box
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Controller
                        control={control}
                        name={`recommendations.${idx}.option`}
                        defaultValue={f.option ?? ""}
                        render={({ field }) => {
                          const optError = errors?.recommendations?.[idx]?.option;
                          return (
                            <FormControl size="small" error={!!optError} sx={{ minWidth: 260 }}>
                              <Select
                                {...field}
                                displayEmpty
                                disabled={readOnly}
                                onChange={(e) => {
                                  field.onChange(e);
                                  // if (e.target.value !== "Other") {
                                  //   setValue(`recommendations.${idx}.otherText`, "");
                                  //   clearErrors(`recommendations.${idx}.otherText`);
                                  // }
                                  if (e.target.value !== "Other") {
                                    setValue(`recommendations.${idx}.other_text`, "");
                                    clearErrors(`recommendations.${idx}.other_text`);
                                  }
                                }}
                                renderValue={(val) => (val ? val : "Select recommendation")}
                              >
                                {recommendationOptions.map((opt) => (
                                  <MenuItem key={opt.value || "none"} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>{optError?.message || " "}</FormHelperText>
                            </FormControl>
                          );
                        }}
                      />

                      {selected === "Other" && (
                        <TextField
                          size="small"
                          label="Specify recommendation"
                          sx={{ flex: "1 1 260px", minWidth: 240 }}
                          // error={!!errors?.recommendations?.[idx]?.otherText}
                          // helperText={errors?.recommendations?.[idx]?.otherText?.message || " "}
                          // disabled={readOnly}
                          // {...register(`recommendations.${idx}.otherText`)}
                          error={!!errors?.recommendations?.[idx]?.other_text}
                          helperText={errors?.recommendations?.[idx]?.other_text?.message || " "}
                          disabled={readOnly}
                          {...register(`recommendations.${idx}.other_text`)}
                        />
                      )}

                      {!readOnly && (
                        <Box sx={{ ml: "auto" }}>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => removeRec(idx)}
                            aria-label="Remove recommendation"
                            disabled={recFields.length <= 1}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {!readOnly && (
              <Button
                size="small"
                variant="outlined"
                disabled={recFields.length >= maxRecommendations}
                onClick={() => {
                  if (recFields.length >= maxRecommendations) return;
                  appendRec({ option: "", other_text: "" });
                }}
                sx={{ mt: 2 }}
              >
                Add Recommendation ({recFields.length}/{maxRecommendations})
              </Button>
            )}

            {errors?.recommendations?.message && (
              <FormHelperText error sx={{ mt: 1 }}>
                {errors.recommendations.message}
              </FormHelperText>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}